import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('keydown', this.#onIllustratedTab);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('keydown', this.#onIllustratedTab);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Keep keyboard focus on visible controls in the illustrated dialog.
   * @param {KeyboardEvent} event
   */
  #onIllustratedTab = (event) => {
    if (event.key !== 'Tab' || !this.isOpen || !this.refs.menuDrawer.classList.contains('menu-drawer--illustrated')) return;

    const container = this.refs.menuDrawer.querySelector('.drawer-localization[open] .menu-drawer__submenu') ?? this.refs.menuDrawer;
    const controls = Array.from(container.querySelectorAll('a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, summary, [tabindex="0"]'))
      .filter((element) => {
        if (!element.getClientRects().length || getComputedStyle(element).visibility !== 'visible') return false;
        // A closed details element can retain child layout boxes. Only its summary is focusable.
        for (let ancestor = element.parentElement; ancestor && ancestor !== container; ancestor = ancestor.parentElement) {
          if (ancestor instanceof HTMLDetailsElement && !ancestor.open && !ancestor.querySelector(':scope > summary')?.contains(element)) return false;
        }
        return true;
      });
    const first = controls[0];
    const last = controls[controls.length - 1];
    const target = event.shiftKey && document.activeElement === first ? last : !event.shiftKey && document.activeElement === last ? first : null;
    if (target instanceof HTMLElement) {
      event.preventDefault();
      target.focus();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details && this.refs.menuDrawer.classList.contains('menu-drawer--illustrated')) {
      const scrollArea = this.refs.menuDrawer.querySelector('.ihh-mobile-menu__scroll');
      if (scrollArea) scrollArea.scrollTop = 0;
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        // Keep the illustrated dialog's trap inside the panel, excluding its background trigger.
        const focusContainer = details === this.refs.details && this.refs.menuDrawer.classList.contains('menu-drawer--illustrated')
          ? this.refs.menuDrawer
          : details;
        trapFocus(focusContainer);
        if (details === this.refs.details && this.refs.menuDrawer.classList.contains('menu-drawer--illustrated')) {
          const closeButton = this.refs.menuDrawer.querySelector('.ihh-mobile-menu__close');
          if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
        }
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    // These inline accordions use the browser's native expanded state, not a sliding submenu.
    if (details.matches('.ihh-mobile-category__branch, .ihh-mobile-category__nested')) {
      details.open = false;
      summary.removeAttribute('aria-expanded');
      summary.focus({ preventScroll: true });
      return;
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          if (this.refs.menuDrawer.classList.contains('menu-drawer--illustrated')) summary.focus({ preventScroll: true });
        } else {
          trapFocus(this.refs.menuDrawer.classList.contains('menu-drawer--illustrated') ? this.refs.menuDrawer : this.refs.details);
          if (this.refs.menuDrawer.classList.contains('menu-drawer--illustrated')) summary.focus({ preventScroll: true });
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  const summary = element.querySelector('summary');
  if (element.matches('.ihh-mobile-category__branch, .ihh-mobile-category__nested')) {
    summary?.removeAttribute('aria-expanded');
  } else {
    summary?.setAttribute('aria-expanded', 'false');
  }
}
